import PeriodSelector from '../PeriodSelector';
import { Typography } from '../../../../../core/components/molecules/Typography';
import { ProgressBar } from '../../../../../core/components/atoms/ProgressBar';
import ProgressDoneItems from '../ProgressDoneItems';
import { useGetHubProgress } from '../../../hooks/ScheduleHub/useGetHubProgress';
import { useEffect, useState } from 'react';
import { TPeriodSelector } from '../../../model/ScheduleHub';
import BrainLoadingWrapper from '../../../../../core/components/molecules/BrainLoading';
import { Icon } from '../../../../../core/components/atoms/Icon';
import { Tooltip } from 'react-tooltip';
import { useUserStore } from '../../../../Auth/stores/authStore';
import { ScheduleStartInfo } from '../../../utils/scheduleStart';

interface IProgressCard {
  cronogramaId?: string;
  startInfo: ScheduleStartInfo;
}

const ProgressCard = ({ cronogramaId, startInfo }: IProgressCard) => {
  const { user } = useUserStore();

  const [period, setPeriod] = useState<TPeriodSelector>(
    startInfo.status === 'offWeek' ? 'month' : 'week'
  );
  const {
    mutate,
    isPending: isPendingProgress,
    isError: isErrorProgress,
    data: progressData,
  } = useGetHubProgress();
  const shouldShowTrophy = period === 'week';

  useEffect(() => {
    if (cronogramaId) {
      mutate({ idCronograma: cronogramaId, period });
    }
  }, [cronogramaId, period]);

  const showContent = () => {
    if (isPendingProgress || !cronogramaId) {
      return <BrainLoadingWrapper size='sm' />;
    }

    if (isErrorProgress || !progressData) {
      return null;
    }

    return (
      <div className='flex flex-col gap-lg mt-10'>
        <div className='relative w-full'>
          {shouldShowTrophy && (
            <div
              className={`
                absolute left-[70%] top-1/2
                hidden -translate-y-1/2 items-center justify-center md:flex
                rounded-pill border-[3px] bg-mono-color-white px-2 py-2 
                ${progressData.progress < 61 ? 'border-mono-color-lightest' : 'border-brand-color-primary-pure'}
              `}
            >
              <Icon
                Icon='trophy'
                type='material'
                variant='fill'
                className={`text-md ${progressData.progress < 61 ? 'text-mono-color-lightest' : 'text-brand-color-primary-pure'} filled`}
              />
            </div>
          )}
          <Icon
            Icon='info'
            type='material'
            id='info-tooltip'
            className={`absolute right-0 top-[calc(100%-25px)] hidden cursor-pointer text-md text-mono-color-dark md:block`}
          />
          <Tooltip
            opacity={1}
            offset={10}
            anchorSelect='#info-tooltip'
            place='right'
            style={{
              width: '220px',
              background: user?.isDarkMode ? '#2E3847' : '#F0F3F7',
              borderRadius: '8px',
              padding: '10px',
              zIndex: 10,
            }}
          >
            <Typography size='mini'>
              Complete 80% das tarefas obrigatórias (videoaulas e exercícios) da
              semana e pontue 20 pontos no Ranking do game.
            </Typography>
            <br />
            <Typography size='mini'>*Material é opcional</Typography>
          </Tooltip>
          <ProgressBar
            progress={progressData.progress}
            color='disciplina-biologia'
            backgroundColor='mono-color-lightest'
          />
        </div>
        <div className='mt-6 flex w-full flex-row items-start justify-between md:items-center md:gap-md'>
          <ProgressDoneItems
            title='Exercícios concluídos'
            icon='edit'
            value={progressData.exercises}
            alignment='left'
          />
          <ProgressDoneItems
            title='Videoaulas assistidas'
            icon='play_circle'
            value={progressData.lessons}
            alignment='center'
          />
          <ProgressDoneItems
            title='Materiais acessados'
            icon='library_books'
            value={progressData.materials}
            alignment='right'
          />
        </div>
      </div>
    );
  };

  return (
    <div
      data-tour="progress-card"
      className={`flex flex-col rounded-lg bg-mono-color-white p-5`}
    >
      <div className='flex w-full flex-col justify-between gap-md md:flex-row md:items-center md:gap-0'>
        <Typography type='title' size='h3'>
          Progresso
        </Typography>
        <PeriodSelector
          period={period}
          backgroundColor='bg-mono-color-lightest'
          buttonColorSchema={{
            textColor: user?.isDarkMode
              ? 'text-[#fff]'
              : 'text-brand-color-primary-pure',
            backgroundColor: 'bg-mono-color-lightest',
            selectedBackgroundColor: 'bg-brand-color-primary-pure',
            selectedTextColor: 'text-[#fff]',
          }}
          onChange={(period) => setPeriod(period)}
        />
      </div>
      {showContent()}
    </div>
  );
};

export default ProgressCard;
